import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import { toast, ToastContainer } from "react-toastify";
import { removeKeys } from "../../utils";
import AmountSection from "./AmountSection";
import CalculationSection from "./CalculationSection";
import { LoanDisbursementModal } from "../../components/modal/LoanDisbursementModal";
import "react-loading-skeleton/dist/skeleton.css";
import { PanDeclarationModal } from "../../components/modal";

const RemittanceCalculator: React.FC = () => {
  const dispatch = useAppDispatch();
  const remitterState = useAppSelector((state) => state.remittance);
  const authState = useAppSelector((state) => state.auth);
  const { calculator } = useAppSelector((state) => state.remittance);
  const skipSummaryFromSyncRef = useRef(false);

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    dispatch(remittanceActions.fetchPurposeListRequested());
    dispatch(remittanceActions.fetchLinkedVendorCodeRequested());
    // dispatch(remittanceActions.fetchCardRateRequested()); commented for multiple calls
  }, []);

  {
    /* Default vendor dropdown selected based on the logged in userid */
  }
  useEffect(() => {
    if (!calculator?.amountCalculation?.referrerVendor) {
      const loggedInVendorKey = authState?.currentUser?.vendor_key;
      const vendorCodeList = remitterState?.calculator?.vendorCode?.list;
      if (loggedInVendorKey && vendorCodeList.length > 0) {
        const matchedVendor = vendorCodeList.find((item: any) => item.userId === loggedInVendorKey);
        if (matchedVendor?.userId) {
          dispatch(
            remittanceActions.setAmountCalculation({
              vendorDropdown: matchedVendor,
              transVendorKey: matchedVendor?.userId,
              vendor_key: matchedVendor?.userId,
              referrerVendor: matchedVendor?.vendorCode,
            })
          );
        }
      }
    }
  }, [
    remitterState?.calculator?.vendorCode?.list,
    authState?.currentUser?.vendor_key,
    calculator?.amountCalculation?.referrerVendor,
    dispatch,
  ]);

  {
    /* Initial API call for getting IBR rates by currency */
  }
  useEffect(() => {
    dispatch(remittanceActions.fetchFxRatesRequested(calculator?.amountCalculation?.dest_currency));
  }, [
    calculator?.amountCalculation?.dest_currency,
    calculator?.amountCalculation?.transaction_purpose,
  ]);

  {
    /* Initial API call for getting latest card rate */
  }
  useEffect(() => {
    dispatch(remittanceActions.fetchCardRateRequested());
  }, [calculator?.amountCalculation?.transaction_purpose]);

  {
    /* Show card rate by currency and purpose */
  }
  useEffect(() => {
    const cardRateList = calculator?.cardRate?.list;
    if (cardRateList && cardRateList.length > 0) {
      const purposeCode = calculator?.amountCalculation?.purposeCode;
      const matchedCardRate = cardRateList.find(
        (item: any) => item.currency === calculator?.amountCalculation?.dest_currency
      );
      const displayCardRate =
        purposeCode == "S0305" ? matchedCardRate?.cardRate : matchedCardRate?.cardRateAd1;
      dispatch(remittanceActions.setCardRateByCurrencyPurpose(displayCardRate + ""));
    }
  }, [
    calculator?.amountCalculation?.dest_currency,
    calculator?.amountCalculation?.transaction_purpose,
  ]);

  {
    /* Update Exchange Rate By Currency and Purpose */
  }
  useEffect(() => {
    if (calculator?.fxRates?.data && calculator?.fxRates?.data?.ibr) {
      const fxRates = calculator?.fxRates?.data;
      const purposeCode = calculator?.amountCalculation?.purposeCode;
      let marginValue = 0;
      let vendorMarginValue = 0;
      switch (purposeCode) {
        case "S1301":
          marginValue = fxRates.margin_value_maintanence ?? 0;
          vendorMarginValue = fxRates.vendor_margin_value_maintanence ?? 0;
          break;
        case "S1302":
          marginValue = fxRates.margin_value_gift ?? 0;
          vendorMarginValue = fxRates.vendor_margin_value_gift ?? 0;
          break;
        default:
          marginValue = fxRates.margin_value ?? 0;
          vendorMarginValue = fxRates.vendor_margin_value ?? 0;
      }

      const totalRate = (fxRates.ibr ?? 0) + marginValue + vendorMarginValue;
      dispatch(
        remittanceActions.setAmountCalculation({
          conversion_rate: parseFloat(totalRate.toFixed(4)),
        })
      );
    }
  }, [calculator?.fxRates?.data, calculator?.amountCalculation?.transaction_purpose]);

  {
    /* getTransactionSummary — only after fxRates + card-rate have finished */
  }
  useEffect(() => {
    const isSend = calculator?.amountCalculation?.changeInputFrom === "send";
    const isReceive = calculator?.amountCalculation?.changeInputFrom === "receive";
    const fxRatesLoading = calculator?.fxRates?.loading;
    const cardRateLoading = calculator?.cardRate?.loading;
    const fxRatesReady = !fxRatesLoading && !!calculator?.fxRates?.data;
    const cardRateReady = !cardRateLoading;

    if (skipSummaryFromSyncRef.current) {
      skipSummaryFromSyncRef.current = false;
      return;
    }

    // Wait for both /remit/fxRates/.../latest and /remit/fxRates/card-rate to complete
    if (!fxRatesReady || !cardRateReady) return;

    if (
      (isSend && calculator?.amountCalculation?.user_send === undefined) ||
      (isReceive && calculator?.amountCalculation?.reciepient_recieve === undefined) ||
      !calculator?.amountCalculation?.referrerVendor ||
      calculator?.amountCalculation?.skipApi
    )
      return;

    const amountCalculation = { ...calculator?.amountCalculation };
    const isUserSendORRecipient =
      isSend && !calculator?.amountCalculation?.lock_reciepient_recieve
        ? "reciepient_recieve"
        : "user_send";
    const keys = [
      isUserSendORRecipient,
      "changeInputFrom",
      "countryDropdown",
      "purposeDropdown",
      "vendorDropdown",
      "lock_reciepient_recieve",
      "skipApi",
    ];
    const formData = removeKeys(amountCalculation, keys);
    formData.declare_amount = Number(formData.declare_amount) || 0;

    const debounce = setTimeout(() => {
      dispatch(remittanceActions.fetchTransactionSummaryRequested(formData));
    }, 500);

    return () => clearTimeout(debounce); // Added timeout for prevent multiple API calls
  }, [
    calculator?.amountCalculation?.user_send,
    calculator?.amountCalculation?.reciepient_recieve,
    calculator?.amountCalculation?.lock_reciepient_recieve,
    // calculator?.amountCalculation?.dest_currency, // avoid rate fluctuation issue
    calculator?.amountCalculation?.transaction_purpose,
    calculator?.amountCalculation?.isEducation,
    calculator?.amountCalculation?.conversion_rate,
    calculator?.amountCalculation?.referrerVendor,
    calculator?.amountCalculation?.isBene,
    calculator?.amountCalculation?.pan_number,
    calculator?.amountCalculation?.declare_amount,
    calculator?.fxRates?.loading,
    calculator?.fxRates?.data,
    calculator?.cardRate?.loading,
  ]);

  useEffect(() => {
    const res = calculator.getTransactionSummary?.data;
    if (!res?.user_send || !res?.reciepient_recieve) return;

    const currentSend = calculator?.amountCalculation?.user_send;
    const currentReceive = calculator?.amountCalculation?.reciepient_recieve;
    if (currentSend === res.user_send && currentReceive === res.reciepient_recieve) return;

    skipSummaryFromSyncRef.current = true;
    dispatch(
      remittanceActions.setAmountCalculation({
        user_send: res.user_send,
        reciepient_recieve: res.reciepient_recieve,
      })
    );
  }, [
    calculator?.getTransactionSummary?.data?.user_send,
    calculator?.getTransactionSummary?.data?.reciepient_recieve,
    dispatch,
  ]);

  useEffect(() => {
    if (calculator?.getTransactionSummary?.error?.type) {
      const errorMessage = calculator?.getTransactionSummary?.error?.type;
      dispatch(
        remittanceActions.setAmountCalculation({
          pan_number: "",
          declare_amount: "",
        })
      );
      dispatch(remittanceActions.resetFetchTransactionSummary());
      toast.error(errorMessage, { toastId: errorMessage });

      // Clear the last dispatched payload so the summary can be re-fetched once
      // the user corrects a value. We intentionally do NOT refetch FX rates here,
      // otherwise the conversion rate the user is editing gets overwritten/reset.
    }
  }, [calculator?.getTransactionSummary?.error?.type]);

  return (
    <div className="flex w-full h-full justify-center mx-auto scale-[0.8] -mt-16">
      <ToastContainer position="top-center" />
      {/* Amount Section */}
      <AmountSection />

      {/* Center Line */}
      <div className="w-1 h-[750px] border-4 border-r mx-16" />

      {/* Calculation Section */}
      <CalculationSection />

      {/* Loan Disbursement Dialog */}
      <LoanDisbursementModal
        open={remitterState?.modalType === "LOAN_DISBURSEMENT"}
        onYes={() => {
          dispatch(remittanceActions.setModalType(""));
          dispatch(remittanceActions.setAmountCalculation({ isEducation: true }));
        }}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
          dispatch(remittanceActions.setAmountCalculation({ isEducation: false }));
        }}
      />

      {/* Add Declaration Dialog */}
      <PanDeclarationModal
        open={remitterState?.modalType === "PAN_DECLARATION"}
        onYes={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
      />
    </div>
  );
};

export default RemittanceCalculator;
